import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { fetchPaperByDoi, searchPapers, type S2Paper } from "@/services/s2-client";

const DOI_RE = /^(?:https?:\/\/(?:dx\.)?doi\.org\/)?(10\.\d{4,9}\/\S+)$/i;
export type PickedPaper = S2Paper & { tldr?: { text?: string } | null };

/** Title or DOI search over S2 (through the backend proxy). */
export function PaperSearchBox({ onPick, label = "Find a paper by title or DOI" }: {
  onPick: (p: PickedPaper) => void;
  label?: string;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PickedPaper[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 3) { setResults([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const doi = query.match(DOI_RE)?.[1];
        if (doi) {
          const p = await fetchPaperByDoi(doi, ctrl.signal);
          setResults(p ? [p] : []);
        } else {
          setResults(((await searchPapers(query, { limit: 8, signal: ctrl.signal }))?.data ?? []).filter((p) => p.paperId));
        }
      } catch {
        if (!ctrl.signal.aborted) setResults([]);
      } finally {
        if (!ctrl.signal.aborted) setBusy(false);
      }
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  return (
    <div className="flex flex-col gap-2">
      <label className="text-base font-medium" htmlFor="paper-search">{label}</label>
      <Input id="paper-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 10.1037/a0012345 or a title" />
      {busy && <p className="text-muted-foreground">Searching…</p>}
      <ul className="flex flex-col gap-1">
        {results.map((p) => (
          <li key={p.paperId ?? p.title}>
            <button type="button" className="w-full rounded-md p-2 text-left hover:bg-muted" onClick={() => { onPick(p); setQ(""); setResults([]); }}>
              <span className="block font-medium">{p.title}</span>
              <span className="block text-muted-foreground">
                {(p.authors ?? []).slice(0, 3).map((a) => a.name).join(", ")}
                {p.year ? ` · ${p.year}` : ""}{p.venue ? ` · ${p.venue}` : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
